import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';

export type LegalDocumentId = 'user-agreement' | 'privacy-policy' | 'personal-data-consent';

/** Абзац или пункт документа, разбитый на текст и ссылки (сайт, почта). */
type Segment = { text: string; route?: string; href?: string };

type RawBlock = string | { list: string[] } | { terms: [string, string][] } | { fields: [string, string][] };

interface RawDocument {
  title: string;
  dateLabel: string;
  date: string;
  note?: string;
  intro?: string[];
  sections: { title: string; blocks: RawBlock[] }[];
}

type Block =
  | { kind: 'p'; parts: Segment[] }
  | { kind: 'list'; items: Segment[][] }
  | { kind: 'terms' | 'fields'; rows: { term: string; parts: Segment[] }[] };

interface LegalDocument {
  title: string;
  dateLabel: string;
  date: string;
  note?: string;
  intro: Segment[][];
  sections: { id: string; title: string; blocks: Block[] }[];
}

/**
 * Тексты документов: русский и казахский — свои, для английского и китайского интерфейса — английский.
 * Лежат в assets/legal, чтобы не увеличивать основной бандл и загружаться только на этих страницах.
 */
const DOCUMENT_LANGUAGE: Record<LanguageCode, 'ru' | 'kk' | 'en'> = {
  ru: 'ru',
  kk: 'kk',
  en: 'en',
  zh: 'en'
};

const LINK_PATTERN = /(https:\/\/bilload\.kz(?:\/[\w-]+)*|[\w.+-]+@[\w-]+\.[\w.]+\w)/g;

/** Соглашение, политика конфиденциальности и согласие на обработку данных — одна страница, документ задаётся в data маршрута. */
@Component({
  selector: 'app-legal-document',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './legal-document.component.html',
  styleUrl: './legal-document.component.scss'
})
export class LegalDocumentComponent implements OnInit, OnDestroy {
  docId: LegalDocumentId = 'user-agreement';
  doc: LegalDocument | null = null;
  loadError = false;

  private readonly cache = new Map<string, LegalDocument>();
  private subscriptions = new Subscription();
  private loadSub?: Subscription;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.data.subscribe(data => {
        this.docId = data['doc'] ?? 'user-agreement';
        this.load(this.languageService.getCurrentLanguageValue());
      })
    );
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(lang => this.load(lang))
    );
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  t(key: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? '' : value;
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }

  private load(lang: LanguageCode): void {
    const url = `/assets/legal/${this.docId}.${DOCUMENT_LANGUAGE[lang] ?? 'ru'}.json`;
    const cached = this.cache.get(url);
    this.loadSub?.unsubscribe();
    this.loadError = false;

    if (cached) {
      this.doc = cached;
      this.cdr.markForCheck();
      return;
    }

    this.loadSub = this.http.get<RawDocument>(url).subscribe({
      next: raw => {
        const doc = this.prepare(raw);
        this.cache.set(url, doc);
        this.doc = doc;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadError = true;
        this.cdr.markForCheck();
      }
    });
  }

  private prepare(raw: RawDocument): LegalDocument {
    return {
      title: raw.title,
      dateLabel: raw.dateLabel,
      date: raw.date,
      note: raw.note,
      intro: (raw.intro ?? []).map(text => this.linkify(text)),
      sections: raw.sections.map((section, i) => ({
        id: `s${i + 1}`,
        title: section.title,
        blocks: section.blocks.map(block => this.prepareBlock(block))
      }))
    };
  }

  private prepareBlock(block: RawBlock): Block {
    if (typeof block === 'string') {
      return { kind: 'p', parts: this.linkify(block) };
    }
    if ('list' in block) {
      return { kind: 'list', items: block.list.map(item => this.linkify(item)) };
    }
    const kind = 'terms' in block ? 'terms' : 'fields';
    const rows = 'terms' in block ? block.terms : block.fields;
    return { kind, rows: rows.map(([term, text]) => ({ term, parts: this.linkify(text) })) };
  }

  /** Ссылки на страницы сайта открываются роутером, почта — через mailto. */
  private linkify(text: string): Segment[] {
    return text.split(LINK_PATTERN).filter(Boolean).map(part => {
      if (part.startsWith('https://bilload.kz')) {
        return { text: part, route: part.slice('https://bilload.kz'.length) || '/' };
      }
      if (/^[\w.+-]+@/.test(part)) {
        return { text: part, href: `mailto:${part}` };
      }
      return { text: part };
    });
  }
}
